import React from "react";
import { CheckCircle, Clock, Server, Activity } from "lucide-react";

const Status: React.FC = () => {
  const systemStatus = [
    {
      name: "HR Server",
      status: "operational",
      uptime: "99.9%",
      responseTime: "120ms",
      icon: Server,
      description: "HR dashboard and management features",
    },
    {
      name: "Job Seeker Server", 
      status: "operational",
      uptime: "99.8%",
      responseTime: "95ms",
      icon: Server,
      description: "Job seeker dashboard and test platform",
    },
    {
      name: "AI Test Generation",
      status: "operational", 
      uptime: "99.5%",
      responseTime: "2.1s",
      icon: Activity,
      description: "AI-powered test creation and assessment",
    },
    {
      name: "Authentication System",
      status: "operational",
      uptime: "100%", 
      responseTime: "45ms",
      icon: CheckCircle,
      description: "User login and security features",
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "operational":
        return "text-green-600 bg-green-100";
      case "degraded":
        return "text-yellow-600 bg-yellow-100";
      case "outage":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="w-5 h-5" />;
      case "degraded":
        return <Clock className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">System Status</h1>
            <p className="text-xl text-gray-600">
              Real-time status of VetriaAI services and platform activity
            </p>
            <div className="mt-6">
              <span className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full font-medium">
                <CheckCircle className="w-4 h-4 mr-2" />
                All Systems Operational
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* System Components */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">System Components</h2>
            <p className="text-gray-600">Current status of all VetriaAI services</p>
          </div>

          <div className="space-y-4">
            {systemStatus.map((component, index) => {
              const Icon = component.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getStatusColor(component.status)}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{component.name}</h3>
                        <p className="text-gray-600">{component.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-8">
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Uptime</div>
                        <div className="font-semibold text-gray-900">{component.uptime}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Response Time</div>
                        <div className="font-semibold text-gray-900">{component.responseTime}</div>
                      </div>
                      <div className={`flex items-center px-3 py-1 rounded-full ${getStatusColor(component.status)}`}>
                        {getStatusIcon(component.status)}
                        <span className="ml-2 font-medium capitalize">{component.status}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Updates */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Recent Updates</h2>
            <p className="text-gray-600">Latest system updates and maintenance activities</p>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">System Performance Optimization</h3>
                    <span className="text-sm text-gray-500">2 hours ago</span>
                  </div>
                  <p className="text-gray-600 mt-1">
                    Enhanced server response times and improved AI test generation speed by 25%.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Activity className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Security Update</h3>
                    <span className="text-sm text-gray-500">1 day ago</span>
                  </div>
                  <p className="text-gray-600 mt-1">
                    Applied latest security patches and enhanced authentication protocols.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Server className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Infrastructure Upgrade</h3>
                    <span className="text-sm text-gray-500">3 days ago</span>
                  </div>
                  <p className="text-gray-600 mt-1">
                    Upgraded server infrastructure to handle increased user load and improve reliability.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Status;